import React, { useMemo } from 'react';
import { ContactListSortOrder, IClientContact } from '@bb/contacts';
import styled from 'styled-components';
import { capitalize, formatPhoneNumber, formatPhoneNumberAsLink } from 'utils';
import {
  Contact,
  InfiniteTable,
  InfiniteTableProps,
  CLIENT_CONTACT_TAGS,
} from 'components';

import { AddressListingCell, LinkCell, TagsCell, NameCell } from './TableCells';

const ContactWrapper = styled.div`
  span:hover {
    text-decoration: underline;
  }
`;

export type ClientContactTableProps = Omit<InfiniteTableProps, 'columns'> & {
  sort?: {
    field: string;
    order: ContactListSortOrder;
  };
  filter?: Record<string, string[]>;
};

const ClientContactTable: React.FC<ClientContactTableProps> = ({
  sort,
  filter,
  ...props
}) => {
  const CLIENT_COLUMNS = useMemo(
    () => [
      {
        title: 'Name',
        dataIndex: 'firstName',
        width: 220,
        sorter: true,
        sortOrder: sort?.field === 'firstName' && sort.order,
        render: (text?: string, record?: any) => (
          <NameCell
            loading={!record}
            firstName={record?.firstName}
            lastName={record?.lastName}
          />
        ),
      },
      {
        title: 'type',
        dataIndex: 'tags',
        width: 150,
        sorter: true,
        sortOrder: sort?.field === 'tags' && sort.order,
        filteredValue: filter?.tags || null,
        filters: Object.values(CLIENT_CONTACT_TAGS).map(({ label, value }) => ({
          text: label,
          value,
        })),
        render: (text?: string, record?: any) => (
          <TagsCell loading={!record} tags={record?.tags} />
        ),
      },
      {
        title: 'phone',
        dataIndex: 'phone',
        width: 200,
        render: (text?: string, record?: IClientContact) => (
          <LinkCell
            loading={!record}
            value={
              record?.phones?.[0]?.number &&
              formatPhoneNumber(record?.phones?.[0]?.number)
            }
            linktext={formatPhoneNumberAsLink(record?.phones?.[0]?.number)}
            tooltip={capitalize(record?.phones?.[0]?.type)}
          />
        ),
      },
      {
        title: 'email',
        dataIndex: 'email',
        width: 250,
        render: (text?: string, record?: any) => (
          <LinkCell loading={!record} value={record?.email} type="email" />
        ),
      },
      {
        title: 'address / listing',
        dataIndex: 'listings',
        width: 380,
        render: (text?: string, record?: any) => (
            <AddressListingCell
              loading={!record}
              listings={record?.listings}
              address={record?.address || record?.companyAddress}
            />
          ),
      },
      {
        title: 'owner',
        dataIndex: 'owner',
        width: 200,
        render: (text?: string, record?: any) => (
          <ContactWrapper onClick={e => e.stopPropagation()}>
            <Contact contact={record?.owner} loading={!record} />
          </ContactWrapper>
        ),
      },
    ],
    [sort, filter],
  );
  return <InfiniteTable {...props} columns={CLIENT_COLUMNS} />;
};

export default ClientContactTable;
